/**
 * CalendarPage
 *
 * Página principal do calendário com visualizações month/week/day/agenda
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarEventResponse } from '@troia-v3/types';
import { useAuth } from '@/contexts/AuthContext';
import { useEventsInRange, useUserCalendarIntegrations, useUpdateUserCalendarPreferences } from '../hooks';
import { useCalendarSync } from '../hooks/useCalendarSync';
import { CalendarView } from '../types';
import { EventFormModal } from '../components/EventFormModal';
import { CalendarHeader } from '../components/CalendarHeader';
import { CalendarSidebar, CalendarFilters } from '../components/CalendarSidebar';
import {
  FilterBottomSheet,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Button,
  OperatorAvatarFilter,
  type OperatorAvatarFilterOperator,
} from '@/shared/components/ui';
import { CalendarIntegrationModal } from '../components/CalendarIntegrationModal';
import { AvailabilitySettingsModal } from '../components/AvailabilitySettingsModal';
import { MonthView } from '../components/MonthView';
import { WeekView } from '../components/WeekView';
import { DayView } from '../components/DayView';
import { AgendaView } from '../components/AgendaView';
import { toast } from 'sonner';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { LeadQuickViewDialog } from '@/modules/crm/leads/components';
import { Icon } from '@/shared/icons'
import { useIsMobile } from '@/shared/hooks'

// A agenda pede TODOS os status ao backend (inclui cancelled) para tê-los
// disponíveis; a visibilidade é refinada pelo filtro client-side da sidebar.
// Disponibilidade do agente e dashboard NÃO passam status → backend mantém o
// default (esconde cancelados). Referência estável para o queryKey.
const AGENDA_STATUSES = ['confirmed', 'tentative', 'cancelled'];

export const CalendarPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: authState } = useAuth();

  // ✅ Scope-based access control (padrão CRM/Chat)
  const userCalendarScope = useMemo(() => {
    const perms = authState.user?.permissions as any;
    if (!perms) return 'own';
    if (perms.calendar?.scope) {
      const s = perms.calendar.scope;
      if (s === 'all' || s === 'team' || s === 'own') return s;
    }
    return 'own';
  }, [authState.user?.permissions]);

  const showOperatorFilter = userCalendarScope !== 'own';

  // ✅ Buscar integrações de calendário do usuário
  const { data: calendarIntegrations = [] } = useUserCalendarIntegrations();

  // ✅ Buscar usuários para filtro
  const { data: usersData } = useUsersDropdown({ status: 'active' });

  // ✅ Update preferences mutation (busca lazy quando modal abrir)
  const updatePreferencesMutation = useUpdateUserCalendarPreferences();

  // ✅ Hook de sincronização em tempo real
  const { isSyncing, currentItem, totalItems, message, isSocketConnected } = useCalendarSync({
    onSyncCompleted: () => {
      // Auto-refresh dos eventos quando sync completar
      queryClient.invalidateQueries({ queryKey: ['calendar-events-range'] });
      toast.success('Calendário atualizado com sucesso!');
    },
    onSyncFailed: (error) => {
      console.error('Sync failed:', error);
    }
  });

  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventResponse | undefined>(undefined);
  const [defaultModalDate, setDefaultModalDate] = useState<Date | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<string>(() =>
    userCalendarScope === 'own' ? (authState.user?.id || 'all') : 'all'
  );
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [integrationProvider, setIntegrationProvider] = useState<string>('');
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Mobile: a Agenda tem anatomia própria (mockup agenda.html) — calbar Lista|Dia +
  // lista de cards agrupados + FAB. NÃO renderiza as grades desktop espremidas.
  const isMobile = useIsMobile();
  const [mView, setMView] = useState<'list' | 'day'>('list');

  // ✅ Transformar usuários para o shape do OperatorAvatarFilter shared (F12)
  const users: OperatorAvatarFilterOperator[] = useMemo(() => {
    if (!usersData) return [];
    return usersData.map(user => ({
      id: user.id,
      name: user.name,
      avatar: user.avatar
    }));
  }, [usersData]);

  // ✅ Ajustar selectedUserId quando scope muda
  useEffect(() => {
    if (userCalendarScope === 'own' && authState.user?.id) {
      setSelectedUserId(authState.user.id);
    }
  }, [userCalendarScope, authState.user?.id]);

  // Filters state
  const [filters, setFilters] = useState<CalendarFilters>({
    integrations: [],
    statuses: [],
    types: [],
  });

  // ✅ Ler parâmetros de URL para navegação direta (ex: de dashboard widget)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const viewParam = searchParams.get('view');

    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime())) {
        setCurrentDate(parsedDate);
      }
    }

    if (viewParam && ['month', 'week', 'day', 'agenda'].includes(viewParam)) {
      setView(viewParam as CalendarView);
    }

    const typeParam = searchParams.get('type');
    if (typeParam) {
      setFilters(prev => ({ ...prev, types: [typeParam] }));
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // Executar apenas uma vez ao montar

  // Detectar sucesso da integração OAuth
  useEffect(() => {
    const result = searchParams.get('result');
    const provider = searchParams.get('provider');

    if (result === 'success' && provider) {
      // Converter providerId para nome amigável (normalizar hífen para underscore)
      const normalizedProvider = provider.toLowerCase().replace(/-/g, '_');
      const providerNames: Record<string, string> = {
        'google_calendar': 'Google Calendar',
        'outlook_calendar': 'Outlook Calendar',
        'apple_calendar': 'Apple Calendar',
        'icloud_calendar': 'iCloud',
      };

      setIntegrationProvider(providerNames[normalizedProvider] || provider);
      setShowSuccessModal(true);

      // Limpar query params da URL
      searchParams.delete('result');
      searchParams.delete('integrationId');
      searchParams.delete('provider');
      setSearchParams(searchParams);

      // Forçar refetch imediato de user-integrations
      queryClient.refetchQueries({ queryKey: ['user-calendar-integrations'] });
    }
  }, [searchParams, setSearchParams, queryClient]);

  // No mobile, a visão efetiva que define a JANELA de busca é o mView próprio
  // (list = agenda/forward; day = dia único), não a `view` das grades desktop.
  const rangeView: CalendarView = isMobile ? (mView === 'day' ? 'day' : 'agenda') : view;

  // Calculate date range based on view
  const getDateRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    switch (rangeView) {
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);  // Start of first day
        end.setMonth(end.getMonth() + 1, 0);  // Last day of month
        end.setHours(23, 59, 59, 999);  // End of last day
        break;
      case 'week':
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        start.setHours(0, 0, 0, 0);  // Start of first day
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);  // End of last day
        break;
      case 'day':
        start.setHours(0, 0, 0, 0);  // Start of day
        end.setHours(23, 59, 59, 999);  // End of day
        break;
      case 'agenda':
        start.setHours(0, 0, 0, 0);  // Start of today
        end.setMonth(end.getMonth() + 1);
        end.setHours(23, 59, 59, 999);  // End of range
        break;
    }

    return {
      startTime: start.toISOString(),
      endTime: end.toISOString()
    };
  };

  const { startTime, endTime } = getDateRange();

  // Fetch events in range (com filtro de usuário se selecionado)
  const { data: events = [], isLoading } = useEventsInRange(
    startTime,
    endTime,
    selectedUserId,  // ✅ Enviar 'all' quando todos operadores selecionados
    AGENDA_STATUSES  // ✅ inclui cancelled; sidebar filtra client-side (CalendarPage:313)
  );

  // Helper function to expand recurring events
  const expandRecurringEvents = (events: CalendarEventResponse[]): CalendarEventResponse[] => {
    const expandedEvents: CalendarEventResponse[] = [];

    events.forEach(event => {
      if (!event.recurrence || event.recurrence.length === 0) {
        // Non-recurring event - add as is
        expandedEvents.push(event);
        return;
      }

      // Parse RRULE
      const rruleString = event.recurrence[0].replace('RRULE:', '');
      const parts = rruleString.split(';');
      let freq: string | undefined;
      let interval = 1;
      let until: Date | undefined;

      parts.forEach(part => {
        const [key, value] = part.split('=');
        if (key === 'FREQ') freq = value;
        if (key === 'INTERVAL') interval = parseInt(value, 10);
        if (key === 'UNTIL') {
          // Parse YYYYMMDD
          const year = parseInt(value.substring(0, 4), 10);
          const month = parseInt(value.substring(4, 6), 10) - 1; // Month is 0-indexed
          const day = parseInt(value.substring(6, 8), 10);
          until = new Date(year, month, day);
        }
      });

      if (!freq) {
        expandedEvents.push(event);
        return;
      }

      // Generate occurrences
      const startDate = new Date(event.startTime);
      const endDate = new Date(event.endTime);
      const duration = endDate.getTime() - startDate.getTime();

      const rangeStart = new Date(startTime);
      const rangeEnd = new Date(endTime);
      const maxOccurrences = 365; // Safety limit
      let occurrenceCount = 0;

      let currentStart = new Date(startDate);

      while (currentStart <= rangeEnd && occurrenceCount < maxOccurrences) {
        if (until && currentStart > until) break;

        // Create occurrence
        const currentEnd = new Date(currentStart.getTime() + duration);

        // Only add occurrence if it's within the requested range
        // (starts in range OR ends in range OR spans the range)
        if (
          (currentStart >= rangeStart && currentStart <= rangeEnd) ||  // Starts within range
          (currentEnd >= rangeStart && currentEnd <= rangeEnd) ||      // Ends within range
          (currentStart < rangeStart && currentEnd > rangeEnd)         // Spans entire range
        ) {
          expandedEvents.push({
            ...event,
            id: `${event.id}-${currentStart.toISOString()}`, // Unique ID for each occurrence
            originalId: event.id, // Preserve original ID for delete operations
            startTime: currentStart.toISOString(),
            endTime: currentEnd.toISOString()
          } as CalendarEventResponse);
        }

        // Calculate next occurrence
        if (freq === 'DAILY') {
          currentStart.setDate(currentStart.getDate() + interval);
        } else if (freq === 'WEEKLY') {
          currentStart.setDate(currentStart.getDate() + (7 * interval));
        } else if (freq === 'MONTHLY') {
          currentStart.setMonth(currentStart.getMonth() + interval);
        } else if (freq === 'YEARLY') {
          currentStart.setFullYear(currentStart.getFullYear() + interval);
        } else {
          break; // Unknown frequency
        }

        occurrenceCount++;
      }
    });

    return expandedEvents;
  };

  // Filter events based on sidebar filters (usuário já filtrado pela API) +
  // busca client-side (sobre os eventos já carregados; não altera a query da API).
  const filteredEvents = useMemo(() => {
    // First expand recurring events
    const expanded = expandRecurringEvents(events);
    const q = searchQuery.trim().toLowerCase();

    return expanded.filter(event => {
      // Filter by integration
      if (filters.integrations.length > 0) {
        // TODO: Check event integration when backend provides this field
        // For now, assume 'local' if no integration specified
        const eventIntegration = (event as any).integration || 'local';
        if (!filters.integrations.includes(eventIntegration)) {
          return false;
        }
      }

      // Filter by status
      if (filters.statuses.length > 0 && event.status) {
        if (!filters.statuses.includes(event.status)) {
          return false;
        }
      }

      // Filter by type
      if (filters.types.length > 0) {
        if (!event.eventType || !filters.types.includes(event.eventType)) {
          return false;
        }
      }

      // Busca textual (título, descrição, local, participantes)
      if (q) {
        const haystacks: (string | undefined)[] = [
          event.summary,
          event.description,
          event.location,
          ...(event.attendees || []).map(a => a.email),
          ...(event.attendees || []).map(a => a.displayName),
        ];
        if (!haystacks.some(h => h && h.toLowerCase().includes(q))) {
          return false;
        }
      }

      return true;
    });
  }, [events, filters, searchQuery]);

  const handleCreateEvent = (defaultDate?: Date) => {
    setSelectedEvent(undefined);
    setDefaultModalDate(defaultDate);
    setIsModalOpen(true);
  };

  // O "+" central do bottom-nav global (mockup) cria evento nesta tela — sem FAB
  // duplicado. Espelha o padrão do CRM (troia:mobile-fab). Sem handler, o + cairia
  // no fallback "Mais".
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); handleCreateEvent(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  const handleEditEvent = (event: CalendarEventResponse) => {
    // Não abrir modal para eventos de aniversário (virtuais) — somente leitura
    if (event.source === 'contact' || event.eventType === 'birthday') {
      toast.info('Evento de aniversário — somente leitura');
      return;
    }

    // Atividades: abrir modal de lead direto (sem redirect)
    if (event.source === 'activity' || event.eventType === 'activity') {
      const meta = (event as any).metadata;
      const entityId = meta?.entityId;
      if (entityId) {
        setSelectedLeadId(entityId);
      }
      return;
    }

    setSelectedEvent(event);
    setDefaultModalDate(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(undefined);
    setDefaultModalDate(undefined);
  };

  const handleModalSuccess = () => {
    // Invalidate calendar events cache to refetch
    queryClient.invalidateQueries({
      queryKey: ['calendar-events-range'],
      exact: false
    });
    handleCloseModal();
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    switch (view) {
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'agenda':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    switch (view) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'agenda':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Navegação mobile: passo por mês na Lista, por dia no Dia (independe de `view`).
  const handleMobilePrev = () => {
    const d = new Date(currentDate);
    if (mView === 'day') d.setDate(d.getDate() - 1);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };
  const handleMobileNext = () => {
    const d = new Date(currentDate);
    if (mView === 'day') d.setDate(d.getDate() + 1);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  // Eventos do dia atual (modo "Dia" mobile) — reusa AgendaView (agrupa por data).
  const mobileDayEvents = useMemo(
    () => filteredEvents.filter((e) => {
      const d = new Date(e.startTime);
      return d.getDate() === currentDate.getDate() &&
        d.getMonth() === currentDate.getMonth() &&
        d.getFullYear() === currentDate.getFullYear();
    }),
    [filteredEvents, currentDate],
  );

  const handleDateSelect = (date: Date) => {
    setCurrentDate(date);
    setMobileSidebarOpen(false);
    if (view === 'month' || view === 'agenda') {
      setView('day');
    }
  };

  const handleMoreEventsClick = (date: Date) => {
    setCurrentDate(date);
    setView('day');
  };

  const handleDateClick = (date: Date) => {
    handleCreateEvent(date);
  };

  const handleSlotClick = (date: Date, hour: number, minute: number) => {
    const slotDate = new Date(date);
    slotDate.setHours(hour, minute, 0, 0);
    handleCreateEvent(slotDate);
  };

  const handleSyncCalendars = () => {
    setIsIntegrationModalOpen(true);
  };

  const handleSaveAvailability = async (preferences: any) => {
    await updatePreferencesMutation.mutateAsync(preferences);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header Full-Width (desktop) — mobile usa a calbar própria abaixo */}
      <div className="hidden lg:block">
        <CalendarHeader
          currentDate={currentDate}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onToday={handleToday}
          view={view}
          onViewChange={setView}
          users={users}
          selectedUserId={selectedUserId}
          onUserChange={setSelectedUserId}
          onNewEvent={() => handleCreateEvent()}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onToggleMobileSidebar={() => setMobileSidebarOpen(v => !v)}
          filters={filters}
          onFiltersChange={setFilters}
          showOperatorFilter={showOperatorFilter}
        />
      </div>

      {/* ===== Calbar MOBILE (mockup agenda.html .m-calbar): ‹ Lista|Dia › + hoje/filtros ===== */}
      <div
        className="lg:hidden flex items-center gap-2 px-3 flex-shrink-0"
        style={{ minHeight: 52, backgroundColor: 'var(--theme-card-background)', borderBottom: '1px solid var(--theme-card-border)' }}
      >
        <button
          onClick={handleMobilePrev}
          className="w-9 h-9 flex items-center justify-center rounded-lg flex-none border"
          style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-muted-text)' }}
          aria-label="Anterior"
          data-testid="calendar-mobile-prev"
        >
          <Icon name="chevron-left" className="w-4 h-4" />
        </button>
        {/* Segmented Lista|Dia */}
        <div className="flex-1 inline-flex rounded-lg p-0.5 gap-0.5" style={{ backgroundColor: 'var(--theme-input-background)' }}>
          {(['list', 'day'] as const).map((k) => {
            const active = mView === k;
            return (
              <button
                key={k}
                onClick={() => setMView(k)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: active ? 'var(--theme-card-background)' : 'transparent',
                  color: active ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                  border: active ? '1px solid var(--theme-card-border)' : '1px solid transparent',
                }}
                data-testid={`calendar-mobile-view-${k}`}
              >
                <Icon name={k === 'list' ? 'list' : 'square'} className="w-4 h-4" />
                {k === 'list' ? 'Lista' : 'Dia'}
              </button>
            );
          })}
        </div>
        <button
          onClick={handleMobileNext}
          className="w-9 h-9 flex items-center justify-center rounded-lg flex-none border"
          style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-muted-text)' }}
          aria-label="Próximo"
          data-testid="calendar-mobile-next"
        >
          <Icon name="chevron-right" className="w-4 h-4" />
        </button>
        <button
          onClick={handleToday}
          className="h-9 px-3 rounded-lg border font-semibold text-sm flex-none"
          style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-card-text)' }}
          data-testid="calendar-mobile-today"
        >
          Hoje
        </button>
        {/* F14: filtro de operador no mobile — chip "Operadores" → bottom-sheet
            embutido no shared (< sm); facepile em tablets (sm–lg) */}
        {showOperatorFilter && (
          <OperatorAvatarFilter
            operators={users}
            selectedId={selectedUserId === 'all' ? null : selectedUserId}
            onSelect={(id) => setSelectedUserId(id ?? 'all')}
            maxVisible={2}
            data-testid="calendar-operators-mobile"
          />
        )}
        <button
          onClick={() => setMobileSidebarOpen(v => !v)}
          className="w-9 h-9 flex items-center justify-center rounded-lg flex-none border"
          style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-muted-text)' }}
          aria-label="Filtros"
          data-testid="calendar-mobile-filters"
        >
          <Icon name="filter" className="w-4 h-4" />
        </button>
      </div>

      {/* ✅ Banner de Progresso de Sincronização */}
      {isSyncing && (
        <div
          className="border-b px-4 py-3"
          style={{
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderColor: 'rgba(139, 92, 246, 0.2)'
          }}
        >
          <div className="flex items-center gap-3 max-w-screen-2xl mx-auto">
            <Icon name="refresh" className="w-4 h-4 animate-spin"
              style={{ color: 'var(--theme-primary)' }} />
            <div className="flex-1">
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {message}
              </p>
              {totalItems > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="flex-1 h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: 'var(--theme-primary)',
                        width: `${(currentItem / totalItems) * 100}%`
                      }}
                    />
                  </div>
                  <span
                    className="text-xs font-mono"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {currentItem}/{totalItems}
                  </span>
                </div>
              )}
            </div>
            {!isSocketConnected && (
              <span
                className="text-xs px-2 py-1 rounded-full"
                style={{
                  backgroundColor: 'rgba(251, 191, 36, 0.1)',
                  color: '#f59e0b'
                }}
              >
                Socket desconectado
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sidebar + Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — inline no desktop (oculto quando recolhido ou em telas < lg) */}
        <div className={isSidebarCollapsed ? 'hidden' : 'hidden lg:block'}>
          <CalendarSidebar
            currentDate={currentDate}
            filters={filters}
            onFiltersChange={setFilters}
            onDateSelect={handleDateSelect}
            onSyncCalendars={handleSyncCalendars}
            onAvailabilitySettings={() => setIsAvailabilityModalOpen(true)}
          />
        </div>

        {/* Filtros no mobile — bottom-sheet só com os 3 grupos (mockup #130) */}
        <FilterBottomSheet
          isOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          hideAbove="lg"
          data-testid="calendar-mobile-sidebar"
        >
          <CalendarSidebar
            variant="filters"
            currentDate={currentDate}
            filters={filters}
            onFiltersChange={setFilters}
            onDateSelect={handleDateSelect}
            onSyncCalendars={handleSyncCalendars}
            onAvailabilitySettings={() => setIsAvailabilityModalOpen(true)}
          />
        </FilterBottomSheet>

        {/* Main Content - Calendar Views */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div
            className="flex-1 overflow-hidden"
            style={{ backgroundColor: 'var(--theme-card-background)' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: 'var(--theme-primary)' }}
                  />
                  <span style={{ color: 'var(--theme-muted-text)' }}>
                    Carregando eventos...
                  </span>
                </div>
              </div>
            ) : (
              <>
                {/* ===== Grades DESKTOP (ocultas no mobile) ===== */}
                <div className="hidden lg:block h-full">
                  {view === 'month' && (
                    <MonthView
                      currentDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEditEvent}
                      onDateClick={handleDateClick}
                      onMoreEventsClick={handleMoreEventsClick}
                    />
                  )}

                  {view === 'week' && (
                    <WeekView
                      currentDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEditEvent}
                      onSlotClick={handleSlotClick}
                    />
                  )}

                  {view === 'day' && (
                    <DayView
                      currentDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEditEvent}
                      onSlotClick={handleSlotClick}
                    />
                  )}

                  {view === 'agenda' && (
                    <AgendaView
                      currentDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEditEvent}
                    />
                  )}
                </div>

                {/* ===== Corpo MOBILE (mockup .m-cal-body): lista de cards agrupados ===== */}
                <div className="lg:hidden h-full">
                  <AgendaView
                    currentDate={currentDate}
                    events={mView === 'day' ? mobileDayEvents : filteredEvents}
                    onEventClick={handleEditEvent}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Event Form Modal */}
      {isModalOpen && (
        <EventFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          event={selectedEvent}
          defaultDate={defaultModalDate}
          onSuccess={handleModalSuccess}
          calendarIntegrations={calendarIntegrations}
        />
      )}

      {/* Lead Quick View Dialog */}
      <LeadQuickViewDialog
        leadId={selectedLeadId}
        isOpen={!!selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
      />

      {/* Calendar Integration Modal */}
      <CalendarIntegrationModal
        isOpen={isIntegrationModalOpen}
        onClose={() => setIsIntegrationModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['user-calendar-integrations'] });
          setIsIntegrationModalOpen(false);
        }}
      />

      {/* Availability Settings Modal */}
      {isAvailabilityModalOpen && (
        <AvailabilitySettingsModal
          isOpen={isAvailabilityModalOpen}
          onClose={() => setIsAvailabilityModalOpen(false)}
          onSave={handleSaveAvailability}
        />
      )}

      {/* Success Modal — Modal shared (F72), título 18 (F74) */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="md"
      >
        <ModalHeader title="Agenda conectada" onClose={() => setShowSuccessModal(false)}>
          <div
            className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
            style={{ backgroundColor: '#ecfdf5' }}
          >
            <Icon name="check-circle" className="w-5 h-5" style={{ color: '#059669' }} />
          </div>
        </ModalHeader>
        <ModalContent>
          <div
            className="rounded-[10px] px-3.5 py-3 text-sm"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #eef1f5', color: '#475569' }}
            data-testid="calendar-integration-success-message"
          >
            Sua conta do <span className="font-semibold">{integrationProvider}</span> foi conectada com sucesso. Os eventos começarão a sincronizar em instantes.
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            variant="primary"
            onClick={() => setShowSuccessModal(false)}
            data-testid="calendar-integration-success-ok"
          >
            Entendido
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

CalendarPage.displayName = 'CalendarPage';
